// Libraries
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useDebouncedCallback,
  usePopover,
  useResponsive,
  useTextInput,
  useToast,
} from '@supermove/hooks';
import {Conversation, ProjectModel} from '@supermove/models';

// App
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import IconButton from '@shared/design/components/IconButton';
import SearchBar from '@shared/design/components/SearchBar';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import ConversationForm from '@shared/modules/Communication/forms/ConversationForm';
import useUpdateConversationMutation from '@shared/modules/Communication/hooks/useUpdateConversationMutation';

const SearchBarContainer = Styled.View`
  flex-direction: row;
  padding-left: 16px;
  padding-right: 16px;
  align-items: center;
`;

const ConversationActions = ({project}: {project: ProjectModel}) => {
  const responsive = useResponsive();
  const actionMenu = usePopover();

  const conversation = project.conversations[0];
  const isConversationOpen = conversation?.status === Conversation.ConversationStatus.OPEN;

  const successToast = useToast({
    ToastComponent: SuccessToast,
  });

  const {form, handleSubmit, submitting} = useUpdateConversationMutation({
    conversationForm: ConversationForm.edit(conversation),
    onSuccess: () => {
      actionMenu.handleClose();
      successToast.handleToast({
        message: isConversationOpen
          ? `${responsive.desktop ? 'Conversation marked' : 'Marked'} as follow up.`
          : `${responsive.desktop ? 'Conversation marked' : 'Marked'} as resolved.`,
      });
    },
    onError: (error) => console.log({error}),
  });

  const handleToggleStatus = () => {
    form.setFieldValue(
      'conversationForm.status',
      isConversationOpen
        ? Conversation.ConversationStatus.CLOSED
        : Conversation.ConversationStatus.OPEN,
    );
    setTimeout(handleSubmit, 0);
  };

  return (
    <ActionMenuPopover
      popover={actionMenu}
      actions={[
        {
          text: isConversationOpen ? 'Mark as resolved' : 'Mark as follow up',
          onPress: handleToggleStatus,
          isLoading: submitting,
        },
      ]}
    >
      <IconButton source={Icon.EllipsisV} isSecondary onPress={actionMenu.handleToggle} />
    </ActionMenuPopover>
  );
};

const ConversationWidgetSearchAndActions = ({
  project,
  setSearchQuery,
  isLoading,
}: {
  project: ProjectModel;
  setSearchQuery: (query: string) => void;
  isLoading: boolean;
}) => {
  const responsive = useResponsive();
  const searchInput = useTextInput();
  const handleChangeQuery = useDebouncedCallback((query) => {
    setSearchQuery(query);
  }, 250);

  return (
    <SearchBarContainer>
      <SearchBar
        inputRef={searchInput?.ref}
        onChangeText={(text) => handleChangeQuery(text)}
        placeholder={'Search'}
        containerStyle={{flex: 1}}
        style={{flex: 1}}
        defaultValue={''}
        isLoading={isLoading}
        isClearable
        isResponsive
      />
      <Space width={12} />
      {responsive.desktop && project.conversations?.[0] && (
        <ConversationActions project={project} />
      )}
    </SearchBarContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ConversationWidgetSearchAndActions.fragment = gql`
  ${ConversationForm.edit.fragment}

  fragment ConversationWidgetSearchAndActions on Project {
    id
    uuid
    conversations {
      id
      status
      ...ConversationForm_edit
    }
  }
`;

export default ConversationWidgetSearchAndActions;
